<template>
  <!-- A hairline, not a shadow, for the same reason PjSheet carries one: the
       float is the cart bar's and on a pure ground the panel and the page are
       the same colour. The border is what makes this an object.

       overflow-hidden so a row's hover fill stops at the radius rather than
       squaring off the corners. -->
  <div class="flex w-72 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-m border bg-card">
    <!-- The same destination either way — the account screen is where you sign
         in — but it says which of the two it is about to be, because "Your
         account" is a promise a signed-out diner cannot collect on. -->
    <component
      :is="props.stayPut ? 'button' : Link"
      ref="destinationEl"
      :aria-current="!props.stayPut && props.current ? 'page' : undefined"
      class="flex items-center gap-3 px-4 py-3.5 text-left pj-tap-row hover:bg-surface-faint"
      :href="props.stayPut ? undefined : accountHref"
      :type="props.stayPut ? 'button' : undefined"
      @click="choose"
    >
      <PjAvatar
        v-if="auth.isSignedIn"
        :initials="auth.initials"
        :src="auth.avatarUrl"
      />

      <span class="flex min-w-0 flex-col gap-0.5">
        <span class="truncate text-title text-ink">{{ heading }}</span>
        <span :class="['truncate text-caption text-muted', isPhoneHint && 'pj-num']">{{ hint }}</span>
      </span>
    </component>

    <!-- The explicit way to the orders list, alongside the account row that also
         lands there. Offered inside a venue too, on Mark's call: it does leave
         the page, taking the basket and the table with it same as the account
         row above already does there, but "check my other bookings" is a real
         enough errand mid-order that hiding the way there was the worse
         tradeoff. -->
    <Link
      v-if="auth.isSignedIn"
      class="flex items-center gap-3 border-t px-4 py-3.5 text-title text-ink pj-tap-row hover:bg-surface-faint"
      :href="ordersHref"
      @click="emit('close')"
    >
      <PjIcon
        class="shrink-0 text-muted"
        :name="PJ_ICON.ORDERS"
      />
      <span class="min-w-0 flex-1 truncate">Orders</span>
      <span
        v-if="count > 0"
        class="pj-num inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-caption text-on-primary"
      >
        {{ count }}
      </span>
    </Link>

    <!-- Same reasoning as Orders above. -->
    <Link
      v-if="auth.isSignedIn"
      class="flex items-center gap-3 border-t px-4 py-3.5 text-title text-ink pj-tap-row hover:bg-surface-faint"
      :href="bookingsHref"
      @click="emit('close')"
    >
      <PjIcon
        class="shrink-0 text-muted"
        :name="PJ_ICON.CALENDAR"
      />
      <span class="min-w-0 flex-1 truncate">Bookings</span>
    </Link>

    <div class="border-t px-4 py-4">
      <PjThemeToggle />
    </div>

    <!-- Last, and quiet. It is the one thing here nobody opened the menu to do
         by accident, and it sits below a control they might well have. -->
    <button
      v-if="auth.isSignedIn"
      class="border-t px-4 py-3.5 text-left text-title text-muted pj-tap-row hover:bg-surface-faint hover:text-ink"
      type="button"
      @click="signOut"
    >
      Sign out
    </button>
  </div>
</template>

<script lang="ts" setup>
import { Link } from '@inertiajs/vue3'
import { computed, onMounted, useTemplateRef } from 'vue'

import PjAvatar from '@/domains/pjazza/components/atoms/pj-avatar/PjAvatar.vue'
import { PJ_ICON } from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.types.ts'
import PjIcon from '@/domains/pjazza/components/atoms/pj-icon/PjIcon.vue'
import PjThemeToggle from '@/domains/pjazza/components/molecules/pj-theme-toggle/PjThemeToggle.vue'
import { usePjazzaAuthStore } from '@/domains/pjazza/store/use-pjazza-auth-store/usePjazzaAuthStore.ts'

/**
 * What sits under You in the tab bar: the way to the account screen, whether
 * you are signed in, and the appearance control.
 *
 * Appearance lives here rather than on the account page because it is a
 * property of the device rather than of the diner — it has to be reachable
 * without signing in, and it is not something anybody navigates to a screen
 * for. A dropdown off the chrome is one tap from anywhere in the Pjazza
 * context; a section at the bottom of the account page was a scroll and a
 * sign-in away from the same setting.
 *
 * This is the only place the toggle lives. PjAccountSheet used to carry a second
 * copy for venue pages, which had no chrome of their own to reach it through;
 * the header is now on every screen, so that copy was the same control twice in
 * one gesture.
 */
const props = defineProps<{
  /** Live orders, so the row can say why it is worth opening. */
  liveOrders?: number
  /** True on the account screen itself. */
  current?: boolean
  /**
   * Inside a venue. The row opens a sheet in place instead of navigating: the
   * account screen is on another host path, and going there mid-order takes the
   * basket and the table with it.
   */
  stayPut?: boolean
}>()

const emit = defineEmits<{ close: []; openAccount: [] }>()

const auth = usePjazzaAuthStore()

const destination = useTemplateRef<{ $el: HTMLElement }>('destinationEl')

const accountHref = computed(() => route('pjazza.you'))

// The same screen the account row lands on — its Orders tab is the default — but
// named as the errand rather than as "your account", for the diner who opened
// this menu to find out where their food is.
const ordersHref = computed(() => route('pjazza.you'))

// Same screen, its Bookings tab named directly — the same `?tab=` useTabRoute
// already reads on the account page itself.
const bookingsHref = computed(() => `${route('pjazza.you')}?tab=bookings`)

const count = computed(() => props.liveOrders ?? 0)

/**
 * Their name if we asked for one at sign-up, and otherwise the generic. An
 * account menu headed by a stranger's own name is the clearest possible answer
 * to "am I signed in as me".
 */
const heading = computed(() => {
  if (!auth.isSignedIn) return 'Sign in'

  return auth.name ?? 'Your account'
})

const choose = (): void => {
  if (props.stayPut) emit('openAccount')

  emit('close')
}

/**
 * Three things it could usefully say, in the order they matter.
 *
 * A live order is the reason someone opens this at all. Failing that, the
 * number is the account — it is the one thing that settles which account this
 * is, and the store already holds it, so saying it costs no request.
 */
const hint = computed(() => {
  if (!auth.isSignedIn) return 'To see your orders. No password.'

  if (props.stayPut) return auth.phone ?? 'Your details'

  if (count.value > 0) return `${count.value} ${count.value === 1 ? 'order' : 'orders'} happening now`

  return auth.phone ?? 'Orders, and your details'
})

const isPhoneHint = computed(() => auth.isSignedIn && count.value === 0 && Boolean(auth.phone))

/**
 * The badge is cleared here as well as by the account screen's own sign-out.
 * This one can be reached from Places, where nothing else would ever put the
 * count back to zero, and a badge counting orders nobody can open is worse
 * than no badge.
 */
const signOut = (): void => {
  auth.signOut()
  emit('close')
}

// Opening a menu with a keyboard has to put the caret in it, or the next Tab
// lands behind the panel that just appeared.
onMounted(() => destination.value?.$el?.focus?.())
</script>
